import React from 'react';
import {useNavigate} from "react-router-dom";
import {useAuth} from "context/AuthContext";
import Dropdown, {DropdownItem} from "components/Dropdown";
import {EyeIcon, PencilIcon, ShieldCheckIcon, TrashIcon} from "@heroicons/react/solid";
import {CurrentUser, User} from "types/User";
import {OperatorSettings} from "../../../types/Operator";
import {useOperator} from "../../../context/OperatorContext";
import {useMutation} from "react-query";
import ngage from "../../../services/ngage";
import {invalidate} from "../../../services/query-client";

// Types ----

type AudienceMemberContextMenuProps = {
  audienceMember: User
  onDelete: () => void
  editDisabled?: boolean
  deleteDisabled?: boolean
  position?: 'left' | 'right'
}

// Helpers ----

function userIsProtected(userId: number, currentUser: CurrentUser | null, settings?: OperatorSettings): boolean {
  return [1, currentUser?.id, settings?.audience.guest_user_id]
    .filter(Boolean)
    .includes(userId)
}

// Component ----

function AudienceMemberContextMenu({audienceMember, onDelete, editDisabled, deleteDisabled, position = 'right'}: AudienceMemberContextMenuProps) {
  const {settings} = useOperator();
  const navigate = useNavigate();
  const auth = useAuth();

  const {mutateAsync: verifyEmail, isLoading: isVerifyingEmail} = useMutation({
    mutationFn: async () => await ngage.users.update(audienceMember.id, {is_verified: true}),
    onSuccess: () => invalidate('users.audience')
  });

  function viewAudienceMember() {
    void navigate(`/audience/${audienceMember.id}`)
  }

  function editAudienceMember() {
    void navigate(`/audience/${audienceMember.id}/edit`)
  }

  return (
    <Dropdown position={position}>
      {auth.can('users:read') &&
        <DropdownItem
          name="View"
          icon={EyeIcon}
          onClick={viewAudienceMember}
        />
      }

      {auth.can('users:write') && !audienceMember.email_is_verified &&
        <DropdownItem
          name="Verify Email"
          icon={ShieldCheckIcon}
          onClick={verifyEmail}
          isLoading={isVerifyingEmail}
        />
      }

      {auth.can('users:write') &&
        <DropdownItem
          name="Edit"
          icon={PencilIcon}
          onClick={editAudienceMember}
          disabled={!!editDisabled}
        />
      }

      {/* TODO: This is a temporary solution to prevent Audience Managers deleting audience members */}
      {(auth.can('users:write') && !auth.roles.includes('audience manager')) &&
        <DropdownItem
          name="Delete"
          icon={TrashIcon}
          onClick={onDelete}
          disabled={!!deleteDisabled || userIsProtected(audienceMember.id, auth.user, settings)}
        />
      }
    </Dropdown>
  );
}

export default AudienceMemberContextMenu;